import React, { useMemo, useState, useEffect } from "react";
import { DataTableEmptyState, DeleteModal } from "CommonComponent";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Row, Col, Image } from "react-bootstrap";
import moment from "moment";
import AddPreventiveActionsModal from "./component/AddPreventiveActionsModal";
import {
  deleteIncidentPreventionAction,
  getIncidentFormValues,
  getIncidentPreventionActionById,
  incidentPreventionActionListFetch,
  redirectToIncidentPreventionActionList,
} from "actions";
import AccessRights from "utils/AccessRights";
import ApproveRejectModal from "./component/ApproveRejectModal";

const PreventiveActionsList = ({ incidentId, incidentTimezone }) => {
  const session = useSelector((state) => state.session);

  const [currentPage, setCurrentPage] = useState(1);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const incidentPreventionActionState = useSelector(
    (state) => state.incidentPreventionActionState
  );
  const [recordId, setRecordId] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showApproveRejectModal, setShowApproveRejectModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [data, setData] = useState([]);

  const isIncidentAdmin = session?.user?.roles?.some(
    (role) => role.name === "Incident Admin"
  );

  useEffect(() => {
    dispatch(incidentPreventionActionListFetch());
  }, [dispatch]);

  useEffect(() => {
    if (
      Array.isArray(incidentPreventionActionState.incidentPreventionActionList)
    ) {
      setData(incidentPreventionActionState.incidentPreventionActionList);
    }
  }, [incidentPreventionActionState.incidentPreventionActionList]);

  useEffect(() => {
    setShowModal(incidentPreventionActionState.showUpdateForm);
  }, [incidentPreventionActionState.showUpdateForm]);

  const showUpdatePopup = (id) => {
    setRecordId(id);
    dispatch(getIncidentPreventionActionById(id, "update"));
    dispatch(getIncidentFormValues());
    setShowModal(true);
  };

  const handleDeleteClick = (id) => {
    setRecordId(id);
    setShowDeleteModal(true);
  };

  const deleteRecord = async () => {
    await dispatch(deleteIncidentPreventionAction(recordId));
    setShowDeleteModal(false);
    setRecordId("");
    dispatch(incidentPreventionActionListFetch());
    dispatch(redirectToIncidentPreventionActionList());
  };

  const preventionActions = useMemo(() => {
    return data.filter((item) => item.incident_id === incidentId);
  }, [data, incidentId]);

  const rowsPerPage = 10;
  const currentData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return preventionActions.slice(start, start + rowsPerPage);
  }, [preventionActions, currentPage]);

  const handleOpenApproveReject = (action) => {
    setSelectedAction(action);
    setShowApproveRejectModal(true);
  };

  if (currentData.length === 0) {
    return (
      <div className="border rounded p-4 text-center">
        <div>
          <img
            src="/assets/images/empty-preventive.png"
            alt=""
            className="text-center"
          />
        </div>
        <button className="add-new-btn" onClick={() => setShowModal(true)}>
          <Image src="/assets/images/add-new-icon.svg" className="me-2" /> Add
          New
        </button>
        <AddPreventiveActionsModal
          incidentId={incidentId}
          incidentTimezone={incidentTimezone}
          show={showModal}
          handleClose={() => setShowModal(false)}
        />
      </div>
    );
  }

  return (
    <>
      {currentData.map((action, index) => (
        <div key={action.id} className="border rounded mb-3 d-flex flex-column">
          {/* Header */}
          <div className="action-content d-flex align-items-center justify-content-between p-3">
            <h2 className="font-13 font-weight-s-bold heading-color mb-0">
              Action# {index + 1}: {action.action_title}
            </h2>

            <div className="d-flex align-items-center">
              {/* Status */}
              {action.imo_approval !== null ? (
                <span
                  className={`me-3 font-13 font-weight-medium ${
                    action.imo_approval === 1 ? "text-success" : "text-danger"
                  }`}
                  style={{ fontWeight: "500" }}
                >
                  {action.imo_approval === 1 ? "Approved" : "Rejected"}
                </span>
              ) : (
                <span
                  className="me-3 text-warning font-13 font-weight-medium"
                  style={{ fontWeight: "500" }}
                >
                  ( Waiting for IMO Approval or Rejection)
                </span>
              )}

              {/* Edit */}
              {action.imo_approval === null &&
                AccessRights(
                  session.current_page_permissions,
                  "edit",
                  action.created_by
                ) &&
                action.is_deleted === "n" && (
                  <Image
                    src="/assets/images/edit-icon.svg"
                    width={20}
                    className="me-3 cursor-pointer"
                    onClick={() => showUpdatePopup(action.id)}
                    title="Edit"
                  />
                )}

              {/* Delete */}
              {action.imo_approval === null &&
                AccessRights(
                  session.current_page_permissions,
                  "delete",
                  action.created_by
                ) &&
                action.is_deleted === "n" && (
                  <Image
                    src="/assets/images/delete-icon.svg"
                    width={20}
                    className="cursor-pointer"
                    onClick={() => handleDeleteClick(action.id)}
                    title="Delete"
                  />
                )}
            </div>
          </div>

          {/* Content */}
          <div className="p-4 flex-grow-1">
            <Row>
              <Col lg={4}>
                <div>
                  <h2 className="font-13 font-weight-normal heading-color">
                    Proposed By:
                  </h2>
                  <p className="font-13 font-weight-normal text-color">
                    <img
                      src="/assets/images/proposed-user.png"
                      className="me-2"
                      alt="proposer"
                    />
                    {action.user?.name || "N/A"}
                  </p>
                </div>
              </Col>

              <Col lg={4}>
                <div>
                  <h2 className="font-13 font-weight-normal heading-color">
                    Priority Level:
                  </h2>
                  <p className="font-13 font-weight-normal text-color">
                    {action.priority_level_name || "N/A"}
                  </p>
                </div>
              </Col>

              <Col lg={4}>
                <div>
                  <h2 className="font-13 font-weight-normal heading-color">
                    Implementation Timeline ({incidentTimezone})
                  </h2>
                  <p className="font-13 font-weight-normal text-color">
                    {action.implementation_time
                      ? moment(action.implementation_time + "Z").format(
                          "YYYY-MM-DD HH:mm"
                        )
                      : "N/A"}
                  </p>
                </div>
              </Col>

              <Col lg={12}>
                <div>
                  <h2 className="font-13 font-weight-normal heading-color">
                    Description:
                  </h2>
                  <p
                    className="font-13 font-weight-normal text-color"
                    dangerouslySetInnerHTML={{
                      __html: action.action_description || "N/A",
                    }}
                  />
                </div>
              </Col>

              {/* 🔹 IMO Comments */}

              <Col lg={12}>
                <div className="mt-2">
                  <h2 className="font-13 font-weight-normal heading-color">
                    IMO Comments:
                  </h2>
                  <p className="font-13 font-weight-normal text-color">
                    {action.imo_comments || "N/A"}
                  </p>
                </div>
              </Col>
            </Row>
          </div>

          {/* Approve / Reject */}
          {action.imo_approval === null && isIncidentAdmin && (
            <div className="d-flex justify-content-end p-2">
              <button
                className="a-r-btn"
                onClick={() => handleOpenApproveReject(action)}
              >
                Approve / Reject
              </button>
            </div>
          )}
        </div>
      ))}

      {/* Add more */}
      <button
        className="bg-transparent border-0 font-13 font-weight-normal"
        onClick={() => setShowModal(true)}
      >
        + Add More
      </button>

      {/* Modals */}
      <DeleteModal
        show={showDeleteModal}
        onDelete={deleteRecord}
        handleClose={() => setShowDeleteModal(false)}
      />
      <AddPreventiveActionsModal
        show={showModal}
        incidentId={incidentId}
        incidentTimezone={incidentTimezone}
        handleClose={() => setShowModal(false)}
      />
      <ApproveRejectModal
        show={showApproveRejectModal}
        handleClose={() => setShowApproveRejectModal(false)}
        action={selectedAction}
      />
    </>
  );
};

export default PreventiveActionsList;
